import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import {
  deleteIntegration,
  listIntegrations,
  TIntegration,
} from "../../../../services/api-integrations";
import { TSession } from "../../../../utils/types";
import TOKENS from "./styles/tokens";

type Props = {
  session: TSession;
  refreshKey: number;
};

const INTEGRATION_NAMES: Record<string, string> = {
  "4demr": "4D EMR",
  drchrono: "DrChrono",
  gohighlevelv1: "GoHighLevel",
  gohighlevelv2: "GoHighLevel",
  meta_pixel: "Meta Pixel",
  pipedrive_api_key: "Pipedrive",
  pipedrive_oauth: "Pipedrive",
  webhook: "Webhook",
};

const getIntegrationName = (integrationType: string) =>
  INTEGRATION_NAMES[integrationType.toLowerCase()] ??
  integrationType
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const confirmDeletion = (integrationName: string): Promise<boolean> => {
  const message = `Delete the ${integrationName} integration? This cannot be undone.`;

  if (Platform.OS === "web" && typeof window !== "undefined") {
    return Promise.resolve(window.confirm(message));
  }

  return new Promise((resolve) => {
    Alert.alert("Delete integration?", message, [
      {
        text: "Cancel",
        style: "cancel",
        onPress: () => resolve(false),
      },
      {
        text: "Delete",
        style: "destructive",
        onPress: () => resolve(true),
      },
    ]);
  });
};

const ExistingIntegrationsSection: React.FC<Props> = ({
  session,
  refreshKey,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [integrations, setIntegrations] = useState<TIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [focusedDeleteId, setFocusedDeleteId] = useState<number | null>(null);

  const loadIntegrations = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      setIntegrations(await listIntegrations(session));
    } catch {
      setLoadError(
        "We couldn't load your connected integrations. Check your connection and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void loadIntegrations();
  }, [loadIntegrations, refreshKey]);

  const handleDelete = async (integration: TIntegration) => {
    const integrationName = getIntegrationName(integration.integration_type);
    if (!(await confirmDeletion(integrationName))) return;

    try {
      setDeletingId(integration.integration_id);
      setLoadError(null);
      await deleteIntegration(session, integration.integration_id);
      setIntegrations((current) =>
        current.filter(
          (item) => item.integration_id !== integration.integration_id,
        ),
      );
    } catch {
      setLoadError(
        `We couldn't delete the ${integrationName} integration. Please try again.`,
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <View
      accessibilityRole="summary"
      style={styles.section}
      testID="existing-integrations-section"
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionHeading}>
          <Text style={styles.title}>Connected integrations</Text>
          <Text style={styles.description}>
            Review and remove integrations currently connected to your clinic.
          </Text>
        </View>
        {!isLoading && !loadError ? (
          <View style={styles.countBadge}>
            <Text style={styles.countText}>{integrations.length}</Text>
          </View>
        ) : null}
      </View>

      {isLoading ? (
        <View style={styles.stateContainer}>
          <ActivityIndicator color={TOKENS.color.focus} size="small" />
          <Text style={styles.stateText}>
            Loading connected integrations...
          </Text>
        </View>
      ) : null}

      {loadError ? (
        <View accessibilityRole="alert" style={styles.errorContainer}>
          <View style={styles.errorCopy}>
            <MaterialIcons
              name="error-outline"
              size={20}
              color={TOKENS.color.error}
            />
            <Text style={styles.errorText}>{loadError}</Text>
          </View>
          <Pressable
            accessibilityRole="button"
            onPress={() => void loadIntegrations()}
            style={({ pressed }) => [
              styles.retryButton,
              pressed ? styles.buttonPressed : null,
            ]}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </Pressable>
        </View>
      ) : null}

      {!isLoading && !loadError && integrations.length === 0 ? (
        <View style={styles.emptyContainer}>
          <View style={styles.emptyIcon}>
            <MaterialIcons
              name="extension"
              size={24}
              color={TOKENS.color.textMuted}
            />
          </View>
          <View style={styles.emptyCopy}>
            <Text style={styles.emptyTitle}>No integrations connected</Text>
            <Text style={styles.stateText}>
              Use the setup wizard below to connect your first integration.
            </Text>
          </View>
        </View>
      ) : null}

      {!isLoading && integrations.length > 0 ? (
        <View style={styles.integrationList}>
          {integrations.map((integration) => {
            const name = getIntegrationName(integration.integration_type);
            const isDeleting = deletingId === integration.integration_id;

            return (
              <View
                key={integration.integration_id}
                style={[
                  styles.integrationRow,
                  isMobile ? styles.integrationRowMobile : null,
                ]}
              >
                <View style={styles.integrationIdentity}>
                  <View style={styles.integrationIcon}>
                    <MaterialIcons
                      name="extension"
                      size={22}
                      color={TOKENS.color.infoText}
                    />
                  </View>
                  <View style={styles.integrationCopy}>
                    <Text style={styles.integrationName}>{name}</Text>
                    <View style={styles.integrationMeta}>
                      <View
                        style={[
                          styles.statusDot,
                          integration.is_active
                            ? styles.statusDotActive
                            : styles.statusDotInactive,
                        ]}
                      />
                      <Text style={styles.integrationStatus}>
                        {integration.is_active ? "Active" : "Inactive"}
                      </Text>
                      <Text style={styles.integrationId}>
                        ID {integration.integration_id}
                      </Text>
                    </View>
                  </View>
                </View>

                <Pressable
                  accessibilityLabel={`Delete ${name} integration`}
                  accessibilityRole="button"
                  accessibilityState={{
                    busy: isDeleting,
                    disabled: isDeleting,
                  }}
                  disabled={isDeleting}
                  onBlur={() => setFocusedDeleteId(null)}
                  onFocus={() => setFocusedDeleteId(integration.integration_id)}
                  onPress={() => void handleDelete(integration)}
                  style={({ pressed }) => [
                    styles.deleteButton,
                    isMobile ? styles.deleteButtonMobile : null,
                    focusedDeleteId === integration.integration_id
                      ? styles.deleteButtonFocused
                      : null,
                    pressed ? styles.buttonPressed : null,
                    isDeleting ? styles.deleteButtonDisabled : null,
                  ]}
                >
                  {isDeleting ? (
                    <ActivityIndicator
                      color={TOKENS.color.error}
                      size="small"
                    />
                  ) : (
                    <MaterialIcons
                      name="delete-outline"
                      size={20}
                      color={TOKENS.color.error}
                    />
                  )}
                  <Text style={styles.deleteButtonText}>
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Text>
                </Pressable>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: TOKENS.color.surface,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    borderWidth: 1,
    gap: TOKENS.spacing.md,
    padding: TOKENS.spacing.lg,
    ...TOKENS.shadow.card,
  },
  sectionHeader: {
    alignItems: "flex-start",
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    justifyContent: "space-between",
  },
  sectionHeading: {
    flex: 1,
    gap: TOKENS.spacing.xxs,
  },
  title: {
    color: TOKENS.color.textPrimary,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.lg,
    lineHeight: TOKENS.lineHeight.lg,
  },
  description: {
    color: TOKENS.color.textSecondary,
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  countBadge: {
    alignItems: "center",
    backgroundColor: TOKENS.color.infoBg,
    borderRadius: TOKENS.radius.pill,
    justifyContent: "center",
    minHeight: 24,
    minWidth: 24,
    paddingHorizontal: TOKENS.spacing.xs,
  },
  countText: {
    color: TOKENS.color.infoText,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
  },
  stateContainer: {
    alignItems: "center",
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
    minHeight: 44,
  },
  stateText: {
    color: TOKENS.color.textSecondary,
    flex: 1,
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  errorContainer: {
    alignItems: "center",
    backgroundColor: TOKENS.color.errorBg,
    borderRadius: TOKENS.radius.md,
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    justifyContent: "space-between",
    padding: TOKENS.spacing.sm,
  },
  errorCopy: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
  },
  errorText: {
    color: TOKENS.color.error,
    flex: 1,
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  retryButton: {
    alignItems: "center",
    backgroundColor: TOKENS.color.surface,
    borderColor: TOKENS.color.error,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    justifyContent: "center",
    minHeight: 44,
    paddingHorizontal: TOKENS.spacing.md,
  },
  retryButtonText: {
    color: TOKENS.color.error,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
  },
  emptyContainer: {
    alignItems: "center",
    backgroundColor: TOKENS.color.surfaceMuted,
    borderRadius: TOKENS.radius.md,
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    minHeight: 72,
    padding: TOKENS.spacing.sm,
  },
  emptyIcon: {
    alignItems: "center",
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.pill,
    height: 44,
    justifyContent: "center",
    width: 44,
  },
  emptyCopy: {
    flex: 1,
    gap: TOKENS.spacing.xxs,
  },
  emptyTitle: {
    color: TOKENS.color.textPrimary,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  integrationList: {
    gap: TOKENS.spacing.xs,
  },
  integrationRow: {
    alignItems: "center",
    backgroundColor: TOKENS.color.surfaceMuted,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    flexDirection: "row",
    gap: TOKENS.spacing.md,
    justifyContent: "space-between",
    minHeight: 72,
    padding: TOKENS.spacing.sm,
  },
  integrationRowMobile: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  integrationIdentity: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
  },
  integrationIcon: {
    alignItems: "center",
    backgroundColor: TOKENS.color.infoBg,
    borderRadius: TOKENS.radius.md,
    height: 44,
    justifyContent: "center",
    width: 44,
  },
  integrationCopy: {
    flex: 1,
    gap: TOKENS.spacing.xxs,
  },
  integrationName: {
    color: TOKENS.color.textPrimary,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.md,
    lineHeight: TOKENS.lineHeight.md,
  },
  integrationMeta: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TOKENS.spacing.xs,
  },
  statusDot: {
    borderRadius: TOKENS.radius.pill,
    height: 8,
    width: 8,
  },
  statusDotActive: {
    backgroundColor: TOKENS.color.success,
  },
  statusDotInactive: {
    backgroundColor: TOKENS.color.textMuted,
  },
  integrationStatus: {
    color: TOKENS.color.textSecondary,
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
  },
  integrationId: {
    color: TOKENS.color.textMuted,
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
  },
  deleteButton: {
    alignItems: "center",
    backgroundColor: TOKENS.color.surface,
    borderColor: TOKENS.color.error,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
    justifyContent: "center",
    minHeight: 44,
    minWidth: 104,
    paddingHorizontal: TOKENS.spacing.sm,
  },
  deleteButtonMobile: {
    alignSelf: "stretch",
  },
  deleteButtonFocused: {
    borderColor: TOKENS.color.focus,
    borderWidth: 2,
  },
  deleteButtonDisabled: {
    backgroundColor: TOKENS.color.disabledBg,
    opacity: 0.7,
  },
  deleteButtonText: {
    color: TOKENS.color.error,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  buttonPressed: {
    opacity: 0.76,
    transform: [{ scale: 0.97 }],
  },
});

export default ExistingIntegrationsSection;
